import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { IHistoryInputModel } from 'inputModels';
import { serialize } from 'object-to-formdata';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { IFileViewModel, IHistoryViewModel } from 'viewModels';
import * as yup from 'yup';
import { ReactComponent as IconAttach } from '../../../assets/icons/icon-attach.svg';
import { useAuth } from '../../../context';
import { useSolicitation } from '../../../context/solicitation';
import { SolicitationController } from '../../../controller/SolicitationController';
import { HistoryType } from '../../../types/enums';
import { Attachment } from '../../Attachment';
import { Button } from '../../Buttons/Button';
import { CustomFile, DropZone } from '../../DropZone';
import { ContentButtons, ContentModal, Header } from './styles';
import { useAlert } from '../../../hooks/useAlert';

interface Props {
  isOpen: boolean;
  solicitationId: string;
  onRequestClose: () => void;
  zIndex?: number;
}
export function ModalAddAttachment({ isOpen, onRequestClose, solicitationId, zIndex }: Props) {
  const { user } = useAuth();
  const alert = useAlert();
  const [requesting, setRequesting] = useState(false);
  const { solicitations, setSolicitations } = useSolicitation();
  const [attachments, setAttachments] = useState<CustomFile[]>([]);

  const defaultValues = {
    text: '',
    attachments: [],
    createdAt: new Date(),
    type: HistoryType.NOTE,
    createdBy: new Date(),
    demandId: '',
    solicitationId: '',
  };

  const registerSchema = yup.object({
    text: yup.string().required('Campo obrigatório'),
    attachments: yup.array(),
    createdAt: yup.date(),
    type: yup.string(),
    createdBy: yup.date(),
    demandId: yup.string(),
    solicitationId: yup.string(),
  });

  const { reset } = useForm({
    resolver: yupResolver(registerSchema),
    reValidateMode: 'onChange',
    defaultValues,
  });

  const handleSendObservation = () => {
    setRequesting(true);
    const history: IHistoryInputModel = {
      text: `Anexo adicionado pelo usuário ${`${user.firstName ?? '-'} ${user.lastName ?? ''}`}`,
      type: HistoryType.ATTACHMENT,
      solicitationId,
    };

    const formData = serialize(history, { indices: true });

    attachments.forEach((attachment) => {
      formData.append('attachments', attachment.file as Blob);
    });

    SolicitationController.addHistory(formData)
      .then((id) => {
        onRequestClose();
        reset();
        alert.show({ message: 'Anexo adicionado com sucesso', type: 'success' });
        const solicitationIdx = solicitations?.findIndex((s) => s.id === solicitationId);
        if (solicitationIdx !== -1) {
          const newAttachments: IFileViewModel[] = attachments.map((attachment) => ({
            id,
            name: attachment.file.name,
            contentType: attachment.file.type,
            totalBytes: attachment.file.size,
            url: attachment.preview,
            file: attachment.file,
            extension: attachment.file.name.split('.').pop(),
          }));
          const newHistory: IHistoryViewModel = {
            id,
            text: history.text || '',
            type: history.type,
            solicitationId,
            createdAt: new Date(),
            visible: true,
            attachments: newAttachments,
          };

          setSolicitations((prev) => {
            const newSolicitations = [...prev];
            newSolicitations[solicitationIdx].history?.push({ ...newHistory });
            newSolicitations[solicitationIdx].history?.sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
            );
            return newSolicitations;
          });
        }
        setAttachments([]);
      })
      .catch(() => {
        onRequestClose();
        alert.show({ message: 'Erro ao adicionar anexo', type: 'error' });
      })
      .finally(() => setRequesting(false));
  };
  const setZIndex =
    zIndex !== undefined
      ? {
          overlay: {
            zIndex: zIndex || 1,
          },
        }
      : {};

  return (
    <Modal
      style={setZIndex}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="react-modal-overlay"
      className="react-add-modal-content">
      <Header>
        <div className="subHeader">
          <IconAttach />
          <h1>Anexar</h1>
        </div>
        <div className="close-div">
          <button type="button" onClick={onRequestClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </Header>
      <ContentModal>
        <DropZone
          type="attachments"
          multipleFiles
          onFiles={(files) => setAttachments((prev) => [...prev, ...files])}
        />
        <div>
          {attachments.map((attachment) => (
            <Attachment
              key={attachment.id}
              data={{
                name: attachment.file.name,
                type: attachment.file.type,
                uri: attachment.preview,
                id: attachment.id,
                file: attachment.file,
              }}
              onDelete={() =>
                setAttachments((prev) => [...prev.filter((a) => a.id !== attachment.id)])
              }
            />
          ))}
        </div>
      </ContentModal>
      <ContentButtons>
        <Button
          text="Enviar"
          style={{ fontSize: '1.20rem', display: 'flex', alignItems: 'center' }}
          dimensions={{
            width: '8rem',
            height: '2rem',
          }}
          positionLeftElement={5}
          border={{ color: 'secondary', width: 0 }}
          color="background_white"
          background="secondary"
          onClick={requesting ? undefined : handleSendObservation}
          disabled={requesting}
        />
      </ContentButtons>
    </Modal>
  );
}
